import classNames from 'classnames'
import React from 'react'
import styles from '../PlayerAccountView.module.css'
import { PinMultipleIcon } from '../../../common/components/icons'
import { getDisplayExercisesAndMaps } from '../../../util/game'
import { GameStatus, TPlayerGameCard } from '../../../types/commonTypes'
import { useTranslation } from 'react-i18next'
import { getFocusableGameItemId } from '../../../composites/GamesOverview/helpers'
import { StatusPill } from '../../../common/components/StatusPill/StatusPill'
import { Tooltip } from '../../../common/components/tooltip/Tooltip'
import { getIcon } from '../../../common/components/icons/utils'
import i18n from '../../../i18n'
import { TFunction } from 'i18next'

type PlayerAccountCardProps = {
  game: TPlayerGameCard
  maxWidth?: string
  minWidth?: string
  disabled?: boolean
}

const getStatusAndColors = (game: TPlayerGameCard, t: TFunction<'translation', undefined, 'translation'>) => {
  if (!game.open) {
    return {
      status: t('player_account_view.status.closed', 'CLOSED'),
      color: 'var(--grey-400)',
      textColor: 'var(--grey-700)',
    }
  }
  if (!game.gamerExerciseCount || game.gamerExerciseCount < 1)
    return {
      status: t('player_account_view.status.not_started', 'NOT STARTED'),
      color: 'white',
      textColor: 'black',
    }
  else if (game.gamerExerciseCount === game.exerciseCount)
    return {
      status: t('player_account_view.status.completed', 'COMPLETED'),
      color: 'var(--green-75)',
      textColor: 'var(--grey-900)',
    }
  else {
    const progress = Math.round((game.gamerExerciseCount / game.exerciseCount) * 100)
    return {
      status: t('player_account_view.status.playing', { defaultValue: '%{progress}% COMPLETED', progress }),
      color: 'var(--primary-normal)',
      textColor: 'white',
    }
  }
}

export const PlayerAccountCard: React.FC<PlayerAccountCardProps> = ({ game, maxWidth, minWidth, disabled }) => {
  const { t } = useTranslation()

  const { id, name, thumbnail } = game

  const cardId = getFocusableGameItemId(id.toString())
  const { status, color, textColor } = getStatusAndColors(game, t)
  const gameURL = `${window.location.protocol}//${window.location.host}/login?pin=${game.playerPin}&l=${i18n.language}`

  const internalOnCardClick = () => {
    window.open(gameURL, '_blank')
  }

  const onCardKeyDownInternal = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      e.key !== 'Enter' ||
      (e.target as EventTarget & { getAttribute: (attr: string) => string })?.getAttribute?.('id') !== cardId
    ) {
      return
    }
    e.preventDefault()
    window.open(gameURL, '_blank')
  }

  return (
    <div style={{ position: 'relative' }}>
      {disabled && <div className={styles.cardDisabledOverlay} />}
      <div
        className={classNames(styles.cardContainer)}
        style={{
          maxWidth,
          minWidth,
          outline: 'solid 1px var(--grey-400)',
        }}
        onClick={internalOnCardClick}
        role='button'
        tabIndex={0}
        onKeyDown={onCardKeyDownInternal}
        id={cardId}
      >
        <div className={styles.top} style={{ backgroundImage: `url('${thumbnail}')` }}>
          <StatusPill
            status={GameStatus.CUSTOM}
            customText={status}
            variant={'card'}
            color={color}
            textColor={textColor}
          />
          {status !== 'COMPLETED' && !disabled && (
            <div>
              <Tooltip tooltipContent={t('game_info.play_game.tooltip', 'Play game')}>
                {(
                  tooltipProps: JSX.IntrinsicAttributes &
                    React.ClassAttributes<HTMLButtonElement> &
                    React.ButtonHTMLAttributes<HTMLButtonElement>,
                ) => (
                  <button className={styles.cardButton} onClick={internalOnCardClick} {...tooltipProps}>
                    {getIcon('play')}
                  </button>
                )}
              </Tooltip>
            </div>
          )}
          {status !== 'COMPLETED' && disabled && (
            <div>
              <button className={styles.cardButtonDisabled} onClick={internalOnCardClick}>
                {getIcon('pause')}
              </button>
            </div>
          )}
        </div>
        <div className={styles.bot}>
          <h5 className={styles.gameTitle}>{name || t('game_info.missing_title_placeholder', 'Game title missing')}</h5>
          <div className={styles.gameInfoContainer}>
            <div className={styles.gameInfo}>
              <PinMultipleIcon />
              <span>{getDisplayExercisesAndMaps(game.exerciseCount, game.mapCount, t)}</span>
            </div>
            {!game.noPointsGame && (
              <div className={styles.gameInfo}>
                <div className={styles.compactIconAndPoints}>
                  <span className={classNames(disabled ? styles.compactPointsIconDisabled : styles.compactPointsIcon)}>
                    {getIcon('scoreHeader')}
                  </span>
                  <span
                    className={classNames(disabled ? styles.compactPointsDisabled : styles.compactPoints)}
                  >{`${game.gamerPoints}/${game.totalPoints}`}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
